/* Chat pane: chats list (left) + LiveChatView (right). */
import React from 'react';
import { Icon, MimeIcon, Avatar } from './primitives.jsx';
import { LiveChatView } from './live-chat.jsx';
import { listChats, deleteChatById, searchChats } from '../api.js';
import { useRouteTitle } from './route-title-context.jsx';
import { subscribeReads, getReads, markChatRead, isChatUnread, syncReadsFromServer } from '../lib/chat-reads.js';

const NEW_LIVE = 'live:new';

export const ChatView = ({ user, autoOpenChat }) => {
  const [activeChat, setActiveChat] = React.useState(autoOpenChat || NEW_LIVE);
  // Deep-link: opening #chat/<id> (e.g. the "Message" button on a profile)
  // jumps straight to that thread even if the chat pane is already mounted.
  React.useEffect(() => {
    if (autoOpenChat) setActiveChat(autoOpenChat);
  }, [autoOpenChat]);
  const [liveChats, setLiveChats] = React.useState([]);
  const [search, setSearch] = React.useState('');
  // searchResults is null until the latest query's response arrives,
  // and is always tagged with the q it was fetched for so the filter
  // can't show stale hits when the user edits the field.
  const [searchResults, setSearchResults] = React.useState(null);
  const [searching, setSearching] = React.useState(false);
  const [deleting, setDeleting] = React.useState(null);
  // Mobile: chat-list is hidden by default; LiveChatView's header
  // shows a "Chats" button that flips this. Auto-closes when the user
  // taps a chat.
  const [listOpenMobile, setListOpenMobile] = React.useState(false);
  const [attachments, setAttachments] = React.useState([]);
  // Stable callback so LiveChatView's effect doesn't re-fire on every render.
  const onAttachmentsChange = React.useCallback(list => setAttachments(list), []);

  // Per-chat read state for the orange unread marks. Subscribed so marks
  // clear the instant a chat is opened (markChatRead emits).
  const [reads, setReads] = React.useState(getReads());
  React.useEffect(() => subscribeReads(setReads), []);

  const reload = React.useCallback(() => {
    listChats()
      .then(({ chats }) => { const list = chats || []; setLiveChats(list); })
      .catch(() => setLiveChats([]));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);
  // Reconcile read marks with the server so they're consistent across browsers.
  React.useEffect(() => { syncReadsFromServer(); }, []);

  // Publish the active chat's title to the route-title context so the
  // breadcrumb in TopHeader shows it as the last segment. Clears when
  // back on the New-chat screen.
  const { setTitle } = useRouteTitle();
  React.useEffect(() => {
    if (activeChat === NEW_LIVE) { setTitle(''); return; }
    const id = activeChat.replace(/^live:/, '');
    const c = liveChats.find(x => x._id === id);
    if (c) setTitle(c.title || 'Untitled chat');
    return () => setTitle('');
  }, [activeChat, liveChats, setTitle]);

  // Debounced server-side search. Tag every response with the q it
  // was fetched for; the render filter only uses results whose tag
  // exactly matches the current query, so editing or clearing the
  // field instantly reverts to the default time-sorted list and no
  // late-arriving response can re-pollute the view.
  React.useEffect(() => {
    const q = search.trim();
    setSearchResults(null);
    if (q.length < 2) { setSearching(false); return; }
    setSearching(true);
    let cancelled = false;
    const handle = setTimeout(() => {
      searchChats(q)
        .then(results => {
          if (cancelled) return;
          setSearchResults({ q, chats: results });
        })
        .catch(() => {
          if (cancelled) return;
          setSearchResults({ q, chats: [] });
        })
        .finally(() => {
          if (cancelled) return;
          setSearching(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search]);

  const liveChatId = activeChat === NEW_LIVE ? null : activeChat.replace(/^live:/, '');

  // The chat you're viewing is, by definition, read — re-stamp whenever it
  // (or its activity) changes so a reply that lands while you're reading
  // doesn't flash an unread mark on the chat you're already in.
  React.useEffect(() => {
    if (liveChatId) markChatRead(liveChatId);
  }, [liveChatId, liveChats]);

  const onLiveChatCreated = id => {
    setActiveChat(`live:${id}`);
    markChatRead(id);
    reload();
  };

  const onDelete = async chat => {
    if (!confirm(`Delete "${chat.title || 'this chat'}"? Messages and attachment references are removed; uploaded files in R2 are kept until the periodic sweeper runs.`)) return;
    setDeleting(chat._id);
    try {
      await deleteChatById(chat._id);
      if (activeChat === `live:${chat._id}`) setActiveChat(NEW_LIVE);
      setLiveChats(prev => prev.filter(c => c._id !== chat._id));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const q = search.trim();
  const hasFreshResults = q.length >= 2 && searchResults && searchResults.q === q;
  const filtered = hasFreshResults ? searchResults.chats : liveChats;
  const hasSide = attachments.length > 0;

  const renderItem = c => {
    const isActive = activeChat === `live:${c._id}`;
    const unread = !isActive && isChatUnread(c, reads);
    return (
    <div
      key={c._id}
      className={`chat-list__item ${isActive ? 'on' : ''} ${unread ? 'chat-list__item--unread' : ''}`}
      onClick={() => { setActiveChat(`live:${c._id}`); markChatRead(c._id); setListOpenMobile(false); }}
    >
      <div className="chat-list__icon">
        {c.kind === 'dm'
          ? <Avatar src={c.photoUrl} name={c.title} size={32}/>
          : <Icon name="chat" size={16}/>}
        {unread && <span className="chat-list__unread-dot" aria-hidden="true"/>}
      </div>
      <div className="chat-list__body">
        <div className="chat-list__row">
          <div className="chat-list__title">{c.title || 'Untitled chat'}</div>
          <span className="chat-list__when">{formatWhen(c.updatedAt)}</span>
        </div>
        {c.snippet && <div className="chat-list__snippet">{c.snippet}</div>}
      </div>
      <button
        type="button"
        title="Delete chat"
        onClick={e => { e.stopPropagation(); onDelete(c); }}
        disabled={deleting === c._id}
        className="chat-list__delete"
      >
        {deleting === c._id ? <span style={{fontSize:12}}>…</span> : <Icon name="trash" size={13}/>}
      </button>
    </div>
    );
  };

  return (
    <div className={`chat-pane ${hasSide ? 'chat-pane--with-side' : ''} ${listOpenMobile ? 'is-history' : ''}`}>
      <div className="chat-list">
        <div className="chat-list__head">
          <div className="chat-list__head-top">
            <h2 className="chat-list__heading">Chats</h2>
            <button
              type="button"
              className="chat-list__new"
              onClick={() => { setActiveChat(NEW_LIVE); setListOpenMobile(false); }}
              aria-label="New chat"
              title="New chat"
            >
              <Icon name="plus" size={18}/>
            </button>
          </div>
          <input
            className="input chat-list__search"
            placeholder="Search chats…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="chat-list__scroll">
          {searching && (
            <div className="chat-list__hint">
              <span className="live-dot" style={{marginRight:6}}/>Searching…
            </div>
          )}

          {filtered.length === 0 && !searching && (
            <div className="chat-list__empty">
              {hasFreshResults ? 'No chats match.' : 'No chats yet. Start one with the “+” button.'}
            </div>
          )}

          {hasFreshResults
            ? <div className="chat-list__group">{filtered.map(renderItem)}</div>
            : groupChatsByDate(filtered).map(([label, items]) => (
                <div key={label} className="chat-list__group">
                  <div className="chat-list__group-head">{label}</div>
                  {items.map(renderItem)}
                </div>
              ))}
        </div>
      </div>

      <LiveChatView
        user={user}
        chatId={liveChatId}
        onChatCreated={onLiveChatCreated}
        onChatTouched={reload}
        onAttachmentsChange={onAttachmentsChange}
        onOpenList={() => setListOpenMobile(true)}
      />

      {hasSide && <ChatArtifactSidePanel attachments={attachments}/>}
    </div>
  );
};

const ChatArtifactSidePanel = ({ attachments }) => (
  <div className="chat-side">
    <div style={{padding:'16px 18px',borderBottom:'1px solid var(--border)'}}>
      <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:4}}>
        Artifacts in this chat
      </div>
      <div style={{fontSize:12,color:'var(--fg-muted)'}}>{attachments.length} file{attachments.length === 1 ? '' : 's'}</div>
    </div>
    <div style={{padding:'12px 14px',display:'flex',flexDirection:'column',gap:8}}>
      {attachments.map(a => (
        <a
          key={a.key}
          href={a.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display:'flex',gap:10,alignItems:'flex-start',
            padding:'10px 12px',background:'var(--bg)',
            border:'1px solid var(--border)',borderRadius:8,
            textDecoration:'none',color:'inherit',
          }}
          title={`${a.name}\n${a.mime || ''}`}
        >
          <MimeIcon mime={a.mime} size={18} tint="var(--fg-muted)"/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12.5,fontWeight:500,wordBreak:'break-word'}}>{a.name}</div>
            <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:2}}>{fmtBytes(a.size)}</div>
          </div>
        </a>
      ))}
    </div>
  </div>
);

function fmtBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// Compact relative time off the last interaction (newest message from the
// user or the platform): now · 3m · 6h · 3 days · 2 weeks · 5 months · 1 year.
function formatWhen(date) {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const min = 60_000, hr = 3_600_000, day = 86_400_000;
  if (diff < min) return 'now';
  if (diff < hr)  return `${Math.floor(diff / min)}m`;
  if (diff < day) return `${Math.floor(diff / hr)}h`;
  const days = Math.floor(diff / day);
  if (days < 7)   return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 30)  { const w = Math.floor(days / 7);   return `${w} week${w === 1 ? '' : 's'}`; }
  if (days < 365) { const mo = Math.floor(days / 30); return `${mo} month${mo === 1 ? '' : 's'}`; }
  const y = Math.floor(days / 365);
  return `${y} year${y === 1 ? '' : 's'}`;
}

// Bucket chats into relative-time sections (Today / Yesterday / …) so the
// list reads like a timeline. Returns [label, chats][] in display order,
// dropping empty sections. Input is assumed newest-first (the API sorts
// by updatedAt desc).
function groupChatsByDate(chats) {
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const day = 86_400_000;
  const buckets = [
    ['Today',           startOfToday],
    ['Yesterday',       startOfToday - day],
    ['Previous 7 days', startOfToday - 7 * day],
    ['Previous 30 days', startOfToday - 30 * day],
    ['Older',           -Infinity],
  ];
  const out = buckets.map(([label]) => [label, []]);
  for (const c of chats) {
    const t = c.updatedAt ? new Date(c.updatedAt).getTime() : 0;
    const idx = buckets.findIndex(([, since]) => t >= since);
    out[idx === -1 ? out.length - 1 : idx][1].push(c);
  }
  return out.filter(([, items]) => items.length > 0);
}
